import { MRT_ColumnDef } from 'mantine-react-table'
import { Text, Badge, Tooltip, Avatar } from '@mantine/core'
import { ReactNode } from 'react'
import getInitials from 'app/views/components/functions/getInitials'
import { ServicingParams, ServicingTypes } from 'app/models/servicing-params'
import LoanRolloverActions from '../deals-management/rollovers/rollover-actions'
import LoanRolloverSplitActions from '../deals-management/rollovers/rollover-split-actions'
import LoanIncreaseActions from '../deals-management/increases/increase-actions'
import FacilityReverseActions from '../deals-management/facility-reverses/facility-reverse-actions'
import LoanActions from '../deals-management/loans/loan-actions'
import LoanRateSettingActions from '../deals-management/rate-settings/rate-setting-actions'
import LoanPrincipalPaymentActions from '../deals-management/principal-payment/principal-payment-actions'
import AddRateSettingActions from '../deals-management/rate-settings/add-rate-setting-actions'
import { FacilityAmortizationServicingActions } from './facility-amortization-servicing-actions'
import PIKActions from '../deals-management/payment-in-kind/pik-actions'
import { FeeFacilityServicingActions } from './fee-facility-servicing-actions'
import FeePaymentPaymentActions from './fee-payment-actions'
import {
  formatDateToShow,
  formatNumberToCurrency,
  stringToDate,
} from 'app/utils/util-functions'
import LoanConversionActions from '../deals-management/conversion/conversion-actions'
import LoanMergeActions from '../deals-management/merges/merge-actions'
import FacilityActions from '../deals-management/facilities/facility-details/facility-actions'
import DealActions from '../deals-management/deals/deal-details/deal-actions'

export enum pillState {
  PendingReview = 'Pending Review',
  Draft = 'Draft',
  Submitted = 'Submitted',
  PendingSubmit = 'Pending Submit',
  PendingApproval = 'Pending Approval',
  Approved = 'Approved',
}

function TableHead({ header }: { header: string }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {header}
    </Text>
  )
}

/** Action menu for a feed row; `original` is the source contract, the row carries the facility facts the menus need. */
function getActionFromType(row: ServicingParams) {
  const data = row.original
  switch (row.type) {
    case ServicingTypes.loanRollover:
      return <LoanRolloverActions row={data} />
    case ServicingTypes.loanRolloverSplit:
      return <LoanRolloverSplitActions row={data} />
    case ServicingTypes.loanIncrease:
      return <LoanIncreaseActions row={data} />
    case ServicingTypes.loanConversion:
      return <LoanConversionActions row={data} />
    case ServicingTypes.loanMerge:
      return <LoanMergeActions row={data} />
    case ServicingTypes.loanRateSetting:
      return <LoanRateSettingActions row={data} />
    case ServicingTypes.loanAddRates:
      return (
        <AddRateSettingActions row={data} facilityStatus={row.facilityStatus} />
      )
    case ServicingTypes.maturingLoan:
    case ServicingTypes.newBorrowing:
    case ServicingTypes.pikScheduleLoan:
      return (
        <LoanActions
          item={data}
          fromOutside={false}
          pikOption={row.facilityPikOption}
        />
      )
    case ServicingTypes.principalPayment:
      return <LoanPrincipalPaymentActions row={data} />
    case ServicingTypes.amortizationSchedule:
      return <FacilityAmortizationServicingActions row={data} />
    case ServicingTypes.fee:
      return <FeeFacilityServicingActions row={data} />
    case ServicingTypes.feePayment:
      return <FeePaymentPaymentActions row={data} />
    case ServicingTypes.pik:
      return <PIKActions row={data} />
    case ServicingTypes.maturingFacility:
      return <FacilityActions row={data} />
    case ServicingTypes.maturingDeal:
      return <DealActions row={data} />
    case ServicingTypes.facilityReverse:
      return <FacilityReverseActions row={data} />
  }
}

function TableRowText({ text }: { text: ReactNode }) {
  return (
    <Text
      style={{
        fontFamily: 'Plus Jakarta Sans',
        fontSize: '1.5em',
      }}
    >
      {text}
    </Text>
  )
}

export const ServicingCols: MRT_ColumnDef<ServicingParams>[] = [
  {
    size: 60,
    accessorKey: 'effectiveDate',
    header: 'Event Date',
    Header: <TableHead header={'Event Date'} />,
    accessorFn: row =>
      row.effectiveDate instanceof Date
        ? row.effectiveDate
        : stringToDate(row.effectiveDate),
    mantineFilterTextInputProps: {
      placeholder: 'Date',
    },
    filterFn: 'lessThanOrEqualTo',
    filterVariant: 'date',
    sortingFn: 'datetime',
    Cell: ({ cell }) => (
      <Text fz="1.5em">{formatDateToShow(cell.getValue<Date>())}</Text>
    ), //render Date as a string
  },
  {
    size: 80,
    accessorKey: 'dealName',
    header: 'Deal Name',
    Header: <TableHead header={'Deal Name'} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
  },
  {
    size: 80,
    accessorKey: 'facilityName',
    header: 'Facility Name',
    Header: <TableHead header={'Facility Name'} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
  },
  {
    size: 80,
    accessorKey: 'stage',
    header: 'Stage',
    Header: <TableHead header={'Stage'} />,
    Cell: ({ cell }) => {
      const state = cell.getValue<string>()
      let styles = {}
      if (state === pillState.PendingReview || state === pillState.Draft) {
        styles = {
          backgroundColor: '#FF5A1F',
        }
      }

      if (state === pillState.PendingSubmit || state === pillState.Submitted) {
        styles = {
          backgroundColor: '#E8006E',
        }
      }
      if (state === pillState.PendingApproval) {
        styles = {
          backgroundColor: '#7E3AF2',
        }
      }
      if (state === pillState.Approved) {
        styles = {
          backgroundColor: '#32439A',
        }
      }

      return (
        <Badge
          styles={{
            root: {
              fontFamily: 'Plus Jakarta Sans',
              color: 'white',
              borderRadius: '4px',
              padding: '1.5em',
              ...styles,
            },
          }}
        >
          <TableRowText text={cell.getValue<string>()} />
        </Badge>
      )
    },
  },
  {
    size: 50,
    accessorKey: 'transaction',
    header: 'Transaction',
    Header: <TableHead header={'Transaction'} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
  },
  {
    size: 90,
    accessorKey: 'amount',
    header: 'Amount',
    Header: <TableHead header={'Amount'} />,
    filterVariant: 'range',
    Cell: ({ cell }) => {
      // a fee's amount is a ledger call after the feed paints: say so
      // rather than show a zero that is not an amount
      if (cell.row.original.amountUnavailable) {
        return <TableRowText text="Unavailable" />
      }
      if (cell.row.original.amountPending) {
        return <TableRowText text="Pending" />
      }
      if (cell.row.original.original.paymentType === 'InterestPayment') {
        return (
          <TableRowText
            text={formatNumberToCurrency(
              cell.row.original.original.interestAmount,
              cell.row.original.currency ? cell.row.original.currency : 'USD',
            )}
          />
        )
      } else {
        return (
          <TableRowText
            text={formatNumberToCurrency(
              cell.row.original.amount,
              cell.row.original.currency ? cell.row.original.currency : 'USD',
            )}
          />
        )
      }
    },
  },
  {
    size: 50,
    accessorKey: 'currency',
    header: 'Currency',
    Header: <TableHead header={'Currency'} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
  },
  {
    size: 50,
    accessorKey: 'notices',
    header: 'Notices',
    Header: <TableHead header={'Notices'} />,
    Cell: ({ cell }) => <TableRowText text={cell.getValue<string>()} />,
  },
  {
    size: 50,
    accessorKey: 'admin',
    header: 'Admin',
    Header: <TableHead header={'Admin'} />,
    Cell: ({ cell }) => {
      const value = cell.getValue()
      if (Array.isArray(value)) {
        return (
          <Avatar.Group spacing="sm">
            {value.map((adminEntity, i) => (
              <Tooltip key={i} label={adminEntity}>
                <Avatar radius="xl" color="cyan" size="lg">
                  {getInitials(adminEntity)}
                </Avatar>
              </Tooltip>
            ))}
          </Avatar.Group>
        )
      } else {
        return (
          <Tooltip label={cell.getValue() as string}>
            <Avatar radius="xl" color="cyan" size="lg">
              {getInitials(cell.getValue<string>())}
            </Avatar>
          </Tooltip>
        )
      }
    },
  },
  {
    size: 40,
    accessorKey: 'id',
    header: 'Actions',
    Header: <TableHead header={'Actions'} />,
    enableColumnActions: false,
    enableColumnFilter: false,
    Cell: cell => getActionFromType(cell.row.original),
  },
]
